import { useState, useEffect } from 'react';
import { Rocket, CheckCircle2, XCircle, AlertTriangle, Loader2, RefreshCw, ThumbsUp, ThumbsDown, Pencil, Download, Copy, ChevronDown, ChevronUp, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = '/api';

function copyText(t) { navigator.clipboard.writeText(t).then(() => toast.success('Gekopieerd')).catch(() => {}); }

const STATUS_STYLE = {
  pass: { icon: CheckCircle2, cls: 'text-green-600 bg-green-50 border-green-200', label: 'Pass' },
  warn: { icon: AlertTriangle, cls: 'text-amber-600 bg-amber-50 border-amber-200', label: 'Warning' },
  fail: { icon: XCircle, cls: 'text-red-600 bg-red-50 border-red-200', label: 'Fail' },
  owner_verify: { icon: AlertTriangle, cls: 'text-blue-600 bg-blue-50 border-blue-200', label: 'Owner verify' },
};

function PreflightPanel({ token }) {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const runChecks = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/ads/preflight`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setResults(data);
    } catch { toast.error('Pre-flight check failed'); }
    finally { setLoading(false); }
  };

  const verdictStyle = results?.verdict === 'ready' ? 'bg-green-50 border-green-300 text-green-800' :
    results?.verdict === 'ready_with_warnings' ? 'bg-amber-50 border-amber-300 text-amber-800' :
    'bg-red-50 border-red-300 text-red-800';

  return (
    <div className="border border-border rounded-xl p-5 bg-white" data-testid="preflight-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-primary" />
          <h3 className="text-base font-semibold">Pre-flight Readiness</h3>
        </div>
        <button onClick={runChecks} disabled={loading}
          className="px-3 py-1.5 text-xs bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1.5"
          data-testid="run-preflight-btn">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Run Checks
        </button>
      </div>

      {results && (
        <>
          <div className={`rounded-lg border px-4 py-3 mb-4 ${verdictStyle}`} data-testid="preflight-verdict">
            <p className="text-sm font-semibold">{results.verdict_message}</p>
            <p className="text-xs mt-1">{results.summary.passed} passed, {results.summary.warnings} warnings, {results.summary.failed} failed</p>
          </div>
          <div className="space-y-2">
            {results.checks.map(c => {
              const st = STATUS_STYLE[c.status] || STATUS_STYLE.warn;
              const Icon = st.icon;
              return (
                <div key={c.id} className={`flex items-start gap-2.5 rounded-lg border px-3 py-2 ${st.cls}`} data-testid={`check-${c.id}`}>
                  <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold">{c.name}</p>
                    <p className="text-[11px] opacity-80">{c.message}</p>
                    {c.layers && (
                      <div className="mt-1 space-y-0.5">
                        {c.layers.map((l, i) => (
                          <p key={i} className="text-[10px]">
                            <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${l.status === 'pass' ? 'bg-green-500' : l.status === 'owner_verify' ? 'bg-blue-400' : 'bg-amber-400'}`} />
                            {l.layer}: {l.detail}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
      {!results && !loading && <p className="text-xs text-muted-foreground">Click "Run Checks" to verify launch readiness.</p>}
    </div>
  );
}

function CampaignCard({ campaign, token, onUpdate }) {
  const [expanded, setExpanded] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [reviseNotes, setReviseNotes] = useState('');
  const [showRevise, setShowRevise] = useState(false);
  const [activationPkg, setActivationPkg] = useState(campaign.activation_package || null);

  const doAction = async (action, body = {}) => {
    setActionLoading(action);
    try {
      const { data } = await axios.post(`${API}/admin/ads/campaigns/${campaign.campaign_id}/${action}`, body, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Campaign ${action}d`);
      if (data.package) setActivationPkg(data.package);
      if (data.campaign) onUpdate(data.campaign);
      else onUpdate(null);
    } catch (e) { toast.error(e.response?.data?.detail || `Failed to ${action}`); }
    finally { setActionLoading(''); }
  };

  const downloadCSV = () => {
    if (!activationPkg?.google_ads_editor_csv) return;
    const blob = new Blob([activationPkg.google_ads_editor_csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${campaign.campaign_name}_google_ads.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const st = campaign.status;
  const statusStyle = st === 'approved' ? 'bg-green-100 text-green-700' :
    st === 'activation_ready' ? 'bg-blue-100 text-blue-700' :
    st === 'rejected' ? 'bg-red-100 text-red-700' :
    st === 'revised' ? 'bg-muted text-muted-foreground' :
    'bg-amber-100 text-amber-700';

  return (
    <div className="border border-border rounded-xl bg-white overflow-hidden" data-testid={`campaign-${campaign.campaign_id}`}>
      <div className="flex items-center justify-between px-5 py-3 cursor-pointer border-b border-border/50" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-3">
          <Rocket className="w-4 h-4 text-primary" />
          <span className="text-sm font-semibold">{campaign.campaign_name}</span>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusStyle}`}>{st}</span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </div>

      {expanded && (
        <div className="p-5 space-y-5">
          {/* Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Goal', value: campaign.campaign_goal },
              { label: 'Audience', value: campaign.target_audience },
              { label: 'Location', value: campaign.location },
              { label: 'Budget', value: `€${campaign.recommended_daily_budget_eur}/day` },
            ].map(item => (
              <div key={item.label} className="p-2.5 bg-muted rounded-lg">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{item.label}</p>
                <p className="text-xs font-medium mt-0.5">{item.value}</p>
              </div>
            ))}
          </div>

          {/* Landing page + UTM */}
          <div className="p-3 bg-muted/50 rounded-lg">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Final URL</p>
            <p className="text-xs font-mono break-all">{campaign.final_url_with_utm}</p>
          </div>

          {/* Keywords */}
          <div>
            <p className="text-xs font-semibold mb-2">Keywords ({campaign.keywords?.length || 0})</p>
            <div className="flex flex-wrap gap-1.5">
              {campaign.keywords?.map((kw, i) => {
                const term = typeof kw === 'string' ? kw : kw.term;
                const match = typeof kw === 'string' ? 'phrase' : kw.match_type;
                const fmt = match === 'exact' ? `"${term}"` : `[${term}]`;
                return (
                  <span key={i} className={`text-[10px] px-2 py-0.5 rounded-full font-mono ${match === 'exact' ? 'bg-blue-100 text-blue-700' : 'bg-primary/10 text-primary'}`}>
                    {fmt}
                  </span>
                );
              })}
            </div>
          </div>

          {/* Negative keywords */}
          <div>
            <p className="text-xs font-semibold mb-2">Negative Keywords ({campaign.negative_keywords?.length || 0})</p>
            <div className="flex flex-wrap gap-1.5">
              {campaign.negative_keywords?.map((nk, i) => (
                <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-red-600 font-mono">-{nk}</span>
              ))}
            </div>
          </div>

          {/* Ad Preview */}
          <div>
            <p className="text-xs font-semibold mb-2">Ad Preview</p>
            <div className="border border-border rounded-lg p-4 bg-white max-w-lg">
              <p className="text-[10px] text-muted-foreground mb-1">Ad · powersure-worldwide.com</p>
              <p className="text-blue-700 text-sm font-medium leading-snug">
                {campaign.headlines?.slice(0, 3).join(' | ')}
              </p>
              <p className="text-xs text-foreground mt-1 leading-relaxed">
                {campaign.descriptions?.[0]}
              </p>
              {campaign.sitelinks?.length > 0 && (
                <div className="flex flex-wrap gap-3 mt-2 pt-2 border-t border-border/50">
                  {campaign.sitelinks.map((sl, i) => (
                    <span key={i} className="text-[10px] text-blue-600">{sl.title}</span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Headlines with char count */}
          <div>
            <p className="text-xs font-semibold mb-2">Headlines</p>
            <div className="space-y-1">
              {campaign.headlines?.map((h, i) => (
                <div key={i} className="flex items-center justify-between text-xs bg-muted/30 rounded px-2.5 py-1.5">
                  <span>{i + 1}. {h}</span>
                  <span className={`text-[10px] ${h.length > 30 ? 'text-red-500 font-bold' : 'text-muted-foreground'}`}>{h.length}/30</span>
                </div>
              ))}
            </div>
          </div>

          {/* Descriptions with char count */}
          <div>
            <p className="text-xs font-semibold mb-2">Descriptions</p>
            <div className="space-y-1">
              {campaign.descriptions?.map((d, i) => (
                <div key={i} className="flex items-center justify-between text-xs bg-muted/30 rounded px-2.5 py-1.5">
                  <span>{i + 1}. {d}</span>
                  <span className={`text-[10px] ${d.length > 90 ? 'text-red-500 font-bold' : 'text-muted-foreground'}`}>{d.length}/90</span>
                </div>
              ))}
            </div>
          </div>

          {/* Sitelinks */}
          <div>
            <p className="text-xs font-semibold mb-2">Sitelinks</p>
            <div className="space-y-1.5">
              {campaign.sitelinks?.map((sl, i) => (
                <div key={i} className="text-xs bg-muted/30 rounded px-2.5 py-1.5">
                  <span className="font-medium text-blue-700">{sl.title}</span>
                  <span className="text-muted-foreground ml-2">— {sl.description}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          {st === 'draft' && (
            <div className="flex items-center gap-2 pt-3 border-t border-border">
              <button onClick={() => doAction('approve')} disabled={!!actionLoading}
                className="px-4 py-2 text-xs bg-green-700 text-white rounded-lg hover:bg-green-800 disabled:opacity-50 flex items-center gap-1.5 font-medium"
                data-testid="approve-campaign-btn">
                {actionLoading === 'approve' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ThumbsUp className="w-3.5 h-3.5" />}
                Approve
              </button>
              <button onClick={() => setShowRevise(!showRevise)} disabled={!!actionLoading}
                className="px-4 py-2 text-xs bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1.5 font-medium"
                data-testid="revise-campaign-btn">
                <Pencil className="w-3.5 h-3.5" /> Revise
              </button>
              <button onClick={() => doAction('reject')} disabled={!!actionLoading}
                className="px-4 py-2 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-1.5 font-medium"
                data-testid="reject-campaign-btn">
                {actionLoading === 'reject' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ThumbsDown className="w-3.5 h-3.5" />}
                Reject
              </button>
            </div>
          )}

          {showRevise && st === 'draft' && (
            <div className="flex items-end gap-2">
              <textarea value={reviseNotes} onChange={e => setReviseNotes(e.target.value)} rows={2} placeholder="Revision instructions..."
                className="flex-1 text-xs border border-border rounded px-2.5 py-2 bg-white resize-none" data-testid="revise-notes" />
              <button onClick={() => { doAction('revise', { notes: reviseNotes }); setShowRevise(false); setReviseNotes(''); }}
                disabled={!reviseNotes.trim() || !!actionLoading}
                className="px-3 py-2 text-xs bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1"
                data-testid="submit-revise-btn">
                {actionLoading === 'revise' ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                Regenerate
              </button>
            </div>
          )}

          {st === 'approved' && (
            <div className="pt-3 border-t border-border">
              <button onClick={() => doAction('activate')} disabled={!!actionLoading}
                className="px-4 py-2 text-xs bg-blue-700 text-white rounded-lg hover:bg-blue-800 disabled:opacity-50 flex items-center gap-1.5 font-medium"
                data-testid="activate-campaign-btn">
                {actionLoading === 'activate' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Rocket className="w-3.5 h-3.5" />}
                Generate Activation Package
              </button>
            </div>
          )}

          {/* Activation Package */}
          {activationPkg && (
            <div className="pt-3 border-t border-border space-y-3" data-testid="activation-package">
              <p className="text-xs font-semibold text-blue-700">Activation Package Ready</p>

              <div className="flex items-center gap-2">
                <button onClick={downloadCSV} className="px-3 py-1.5 text-[10px] border border-primary text-primary rounded hover:bg-primary/5 flex items-center gap-1" data-testid="download-csv-btn">
                  <Download className="w-3 h-3" /> Google Ads Editor CSV
                </button>
                <button onClick={() => copyText(activationPkg.copy_paste_format)} className="px-3 py-1.5 text-[10px] border border-primary text-primary rounded hover:bg-primary/5 flex items-center gap-1" data-testid="copy-paste-btn">
                  <Copy className="w-3 h-3" /> Copy All
                </button>
              </div>

              {/* Launch Checklist */}
              {activationPkg.launch_checklist && (
                <div>
                  <p className="text-xs font-semibold mb-2">Launch Checklist</p>
                  <div className="space-y-2">
                    {activationPkg.launch_checklist.map(step => (
                      <div key={step.step} className="bg-muted/30 rounded-lg px-3 py-2">
                        <p className="text-xs font-semibold text-primary">Step {step.step}: {step.title}</p>
                        <ul className="mt-1 space-y-0.5">
                          {step.items.map((item, i) => (
                            <li key={i} className={`text-[10px] ${item.startsWith('MANUAL') ? 'text-amber-700 font-medium' : 'text-muted-foreground'}`}>
                              {item.startsWith('MANUAL') ? '⚠ ' : '• '}{item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Copy-paste preview */}
              <details className="group">
                <summary className="text-[10px] text-primary cursor-pointer hover:underline">Show full copy-paste format</summary>
                <pre className="mt-1 text-[9px] bg-muted rounded-lg p-3 overflow-x-auto whitespace-pre-wrap max-h-60 overflow-y-auto">{activationPkg.copy_paste_format}</pre>
              </details>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CampaignLaunchPanel({ token }) {
  const [campaigns, setCampaigns] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadCampaigns = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/ads/campaigns`, { headers: { Authorization: `Bearer ${token}` } });
      setCampaigns(data.campaigns || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { loadCampaigns(); }, [token]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/admin/ads/generate-campaign`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (data.campaign) {
        setCampaigns(prev => [data.campaign, ...prev]);
        toast.success('Campaign package generated');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Generation failed'); }
    finally { setGenerating(false); }
  };

  const activeCampaigns = campaigns.filter(c => !['rejected', 'revised'].includes(c.status));

  return (
    <div className="space-y-4" data-testid="campaign-launch-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Rocket className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>Campaign Launch Bot</h2>
        </div>
        <button onClick={handleGenerate} disabled={generating}
          className="px-4 py-2 text-xs bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1.5 font-medium"
          data-testid="generate-campaign-btn">
          {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Rocket className="w-3.5 h-3.5" />}
          {generating ? 'Generating...' : 'Generate Campaign'}
        </button>
      </div>

      <PreflightPanel token={token} />

      {/* Google Ads Editor Import Package */}
      <div className="border border-border rounded-xl p-5 bg-white" data-testid="import-package-panel">
        <div className="flex items-center gap-2 mb-3">
          <Download className="w-4 h-4 text-primary" />
          <h3 className="text-base font-semibold">Google Ads Editor Import Package</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-3">Download CSV files and import in Google Ads Editor in order. Assets (logo + images) must be added manually.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
          {[
            { file: 'A1_campaign_adgroup.csv', label: '1. Campaign + Ad Group' },
            { file: 'A2_keywords.csv', label: '2. Keywords (15)' },
            { file: 'A3_negative_keywords.csv', label: '3. Neg. Keywords (8)' },
            { file: 'A4_responsive_search_ad.csv', label: '4. Search Ad (RSA)' },
            { file: 'A5_sitelinks.csv', label: '5. Sitelinks (4)' },
            { file: 'A6_callouts.csv', label: '6. Callouts (4)' },
            { file: 'IMPORT_GUIDE.md', label: 'Import Guide' },
          ].map(f => (
            <a key={f.file} href={`${API}/admin/ads/download-package/${f.file}`}
              target="_blank" rel="noopener noreferrer"
              className="px-3 py-2 text-[10px] font-medium border border-primary/30 text-primary rounded-lg hover:bg-primary/5 flex items-center gap-1 justify-center"
              data-testid={`download-${f.file}`}
              onClick={e => {
                e.preventDefault();
                fetch(`${API}/admin/ads/download-package/${f.file}`, { headers: { Authorization: `Bearer ${token}` } })
                  .then(r => r.blob())
                  .then(blob => { const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = f.file; a.click(); URL.revokeObjectURL(url); });
              }}>
              <Download className="w-3 h-3" /> {f.label}
            </a>
          ))}
        </div>
        <details className="group">
          <summary className="text-[10px] text-primary cursor-pointer hover:underline">Import order & asset instructions</summary>
          <div className="mt-2 text-[10px] text-muted-foreground space-y-1.5 bg-muted/30 rounded-lg p-3">
            <p className="font-semibold text-foreground">CSV Import (Google Ads Editor):</p>
            <p>1. Account &gt; Import &gt; From file &gt; import A1 through A6 in order</p>
            <p>2. Verify: 1 campaign, 1 ad group, 15 keywords, 8 neg. keywords, 1 RSA, 4 sitelinks, 4 callouts</p>
            <p>3. Post to Google Ads</p>
            <p className="font-semibold text-foreground mt-2">Assets (add manually after CSV import):</p>
            <p>Logo: Upload your Powersure logo (square, 1200x1200px)</p>
            <p>Images (3-5): Use your banners or stock photos. Min 1200x628px landscape.</p>
            <p>In Editor: select campaign &gt; Assets &gt; Images &gt; + upload</p>
            <p>Or in web UI: Ads &amp; Assets &gt; Assets &gt; +</p>
          </div>
        </details>
      </div>

      {loading && <div className="text-center py-4"><Loader2 className="w-6 h-6 text-primary animate-spin mx-auto" /></div>}

      {activeCampaigns.length > 0 && (
        <div className="space-y-3">
          {activeCampaigns.map(c => (
            <CampaignCard key={c.campaign_id} campaign={c} token={token} onUpdate={(updated) => {
              if (updated) setCampaigns(prev => prev.map(p => p.campaign_id === updated.campaign_id ? updated : p));
              else loadCampaigns();
            }} />
          ))}
        </div>
      )}

      {!loading && activeCampaigns.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <p className="text-sm">No campaigns yet. Click "Generate Campaign" to create your first launch package.</p>
        </div>
      )}
    </div>
  );
}
